import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Gauge } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";

const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "—");
const cap = (s) => (s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** A permanent, shareable page per county: what an acre is worth, how sure we are, and who can reach it. */
export default function LandValue() {
  const { county: slug } = useParams();
  const county = cap(slug || "nairobi");
  const [acres, setAcres] = useState("1");
  const [location, setLocation] = useState("peri_urban");
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);

  useSeo({
    title: `Land Value In ${county} — What An Acre Is Worth — KickStartKenyaFootball`,
    description: `A published value range for land in ${county} County, with the confidence stated, the sources named, the ground rent it implies, and how many people can reach a pitch built on it.`,
  });

  useEffect(() => {
    api.get(`/land-estimate?county=${encodeURIComponent(county)}&acres=${parseFloat(acres || "1") || 1}&location=${location}`)
      .then((r) => { setD(r.data); setMissing(false); })
      .catch((e) => { setD(null); setMissing(e?.response?.status === 404); });
  }, [county, acres, location]);

  if (missing) {
    return (
      <div className="bg-black py-24" data-testid="land-value-unknown">
        <div className="mx-auto max-w-2xl px-5 md:px-10">
          <p className="overline text-ochre">Not a county</p>
          <h1 className="display-2 mt-5">Kenya Has 47 Counties, And That Is Not One Of Them</h1>
          <p className="body-copy mt-6">
            We only price land in the 47 counties of the 2019 census. Pick a county and we will price it.
          </p>
          <Link to="/lease-comparison" className="btn-primary mt-8 inline-flex" data-testid="land-value-unknown-back">
            Price land by county
          </Link>
        </div>
      </div>
    );
  }

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="land-value-loading" />;
  const c = d.catchment;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="land-value-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">{county} County</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
          <div className="mt-8 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">Acres</span>
              <input value={acres} onChange={(e) => setAcres(e.target.value.replace(/[^\d.]/g, ""))}
                inputMode="decimal" data-testid="land-value-acres"
                className="mt-2 w-28 border border-white/20 bg-transparent px-3 py-2.5 text-sm text-white focus:border-ochre focus:outline-none" />
            </label>
            <label className="block">
              <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">Where the land is</span>
              <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="land-value-location"
                className="mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none">
                {d.locations.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </label>
          </div>
        </Reveal>

        <section className="mt-12" data-testid="land-value-range">
          <div className="grid gap-px bg-zinc-800 sm:grid-cols-3">
            {[["Low", d.low_shillings], ["Our mid-point", d.mid_shillings], ["High", d.high_shillings]]
              .map(([k, v], i) => (
                <div key={k} className="bg-[#0b0b0b] p-7" data-testid={`land-value-stat-${i}`}>
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
                  <p className={`mono mt-3 text-lg ${i === 1 ? "text-ochre" : "text-white"}`}>{ksh(v)}</p>
                </div>
              ))}
          </div>
          <div className="mono mt-6 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400"
            data-testid="land-value-confidence">
            <Gauge size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span>
              <VerifyChip v="estimate" /> {d.confidence_label} · range ±{d.spread_pct}% · {d.confidence_note}
            </span>
          </div>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
            {ksh(d.per_acre_shillings)} an acre on our model (county index {d.cost_index}). Held on a lease, the
            ground rent for {d.acres} {d.acres === 1 ? "acre" : "acres"} would be {ksh(d.rent_year_shillings)} a
            year at {d.ground_yield_pct}% of value. {d.caveat}
          </p>
          {d.ministry ? (
            <div className={`mt-6 max-w-3xl border-l-2 p-5 ${d.ministry.defers ? "border-ochre bg-[#12100a]" : "border-zinc-700 bg-[#0b0b0b]"}`}
              data-testid="land-value-ministry">
              <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">
                {d.ministry.headline}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                <span className="text-white">{d.ministry.stage_label}.</span> {d.ministry.note}
              </p>
              <a href={d.ministry.source.url} target="_blank" rel="noreferrer"
                className="mono mt-3 inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
                data-testid="land-value-ministry-source">
                {d.ministry.source.name} <ArrowUpRight size={11} />
              </a>
            </div>
          ) : null}
        </section>

        <section className="mt-16" data-testid="land-catchment">
          <p className="eyebrow">Who can reach it</p>
          <h2 className="display-3 mt-4">{c.heading}</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">{c.intro}</p>
          <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-3">
            {c.bands.map((b) => (
              <div key={b.minutes} className="bg-[#0b0b0b] p-7" data-testid={`catchment-${b.minutes}`}>
                <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">{b.minutes} minutes</p>
                <p className="mono mt-3 text-lg text-white">{b.people.toLocaleString()} people</p>
                <p className="mt-3 text-xs leading-relaxed text-zinc-400">
                  {b.radius_km} km out · {b.youth_15_34.toLocaleString()} aged 15-34 ·{" "}
                  {b.teams_supported.toLocaleString()} Sunday-league teams supported
                  {b.capped_by_county ? " · capped at the county's own population" : ""}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{c.method} {c.caveat}</p>
        </section>

        <section className="mt-16 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-7" data-testid="land-value-correct-us">
          <p className="eyebrow">Correct our number</p>
          <h2 className="display-3 mt-4">If This Figure Is Wrong For {county}, Say So</h2>
          <p className="mt-5 text-sm leading-relaxed text-zinc-300">
            This is modelled from published county data, not from a valuation, so in some wards it will be wrong.
            If you know what an acre near you actually sold, let or was asked for, send the price and where it
            was published and we will correct this page and date the change. Send the ward and the acreage
            &mdash; never a plot or title number, and never anybody&rsquo;s contact details.
          </p>
          <Link to={`/apply`} className="btn-primary mt-6 inline-flex items-center gap-2"
            data-testid="land-value-correct-us-cta">
            Send a real price <ArrowUpRight size={14} />
          </Link>
        </section>

        <section className="mt-16 max-w-3xl" data-testid="land-value-sources">
          <p className="eyebrow">Where a better number comes from</p>
          <h2 className="display-3 mt-4">Check It Against These</h2>
          <div className="mt-7 space-y-px bg-zinc-800">
            {d.sources.map((s) => (
              <div key={s.name} className="bg-[#0b0b0b] p-5" data-testid={`land-value-source-${s.name.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <a href={s.url} target="_blank" rel="noreferrer"
                  className="mono inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white">
                  {s.name} <ArrowUpRight size={11} />
                </a>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">{s.note}</p>
              </div>
            ))}
          </div>
          <div className="mono mt-8 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
            <Link to={`/lease-comparison?county=${encodeURIComponent(county)}`} className="text-ochre hover:text-white" data-testid="land-value-to-lease">Price every route</Link>
            <Link to="/site-appraisal" className="text-ochre hover:text-white" data-testid="land-value-to-appraisal">Appraise a plot</Link>
            <Link to="/land-comparables" className="text-ochre hover:text-white" data-testid="land-value-to-comparables">Check us against real deals</Link>
            <Link to={`/idle-acre?county=${encodeURIComponent(county)}&acres=${parseFloat(acres) || 1}&location=${location}`} className="text-ochre hover:text-white" data-testid="land-value-to-idle-acre">Get the one-page sheet</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
